import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Eye, EyeOff, AlertTriangle, Mail, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { SiteLayout, FLAME, INK, BODY, MUTED, BORDER, MONO } from './SiteLayout';

const SUPPORT_EMAIL = 'IgnitorAds.Support@gmail.com';

export default function LoginSitePage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [suspended, setSuspended] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  async function handleSubmit(e) {
    e.preventDefault();
    if (!email || !password) return setError('Email and password are required');
    setLoading(true); setError(null); setSuspended(false);
    try {
      await login(email.trim(), password);
      navigate('/dashboard');
    } catch (err) {
      const isSusp = err.response?.data?.suspended;
      if (isSusp) { setSuspended(true); setError(err.response?.data?.reason || 'Account suspended'); }
      else setError(err.response?.data?.error || 'Invalid email or password');
    } finally { setLoading(false); }
  }

  const inputStyle = {
    width: '100%', height: '46px', padding: '0 14px 0 40px', fontSize: '14px',
    color: INK, background: '#fbfaf7', border: `1px solid #ddd9d0`,
    borderRadius: '10px', boxSizing: 'border-box',
    fontFamily: "'Space Grotesk', sans-serif",
  };
  const labelStyle = { display: 'block', fontSize: '15px', color: BODY, marginBottom: '7px', fontWeight: 600 };
  const iconStyle = { position: 'absolute', left: '14px', top: '50%', transform: 'translateY(-50%)', color: '#a8a297' };

  return (
    <SiteLayout>
      <section style={{
        maxWidth: '520px', margin: '0 auto',
        padding: '80px 32px 90px', minHeight: '60vh',
      }}>
        <div style={{ textAlign: 'center', marginBottom: '32px' }}>
          <p style={{ fontFamily: MONO, fontSize: '16px', letterSpacing: '1.6px', color: FLAME, textTransform: 'uppercase', margin: 0 }}>
            Sign in
          </p>
          <h1 style={{ fontSize: '40px', fontWeight: 600, letterSpacing: '-1.2px', margin: '16px 0 0' }}>
            Welcome back.
          </h1>
          <p style={{ fontSize: '17px', color: BODY, marginTop: '12px', lineHeight: 1.55 }}>
            Enter your company credentials to open the AdTech Hub.
          </p>
        </div>

        <div style={{
          background: 'rgba(255,255,255,.9)', border: `1px solid ${BORDER}`,
          borderRadius: '18px', padding: '32px', backdropFilter: 'blur(8px)',
          boxShadow: '0 20px 44px -28px rgba(40,30,18,.34)',
        }}>
          {suspended && (
            <div style={{
              background: '#fff5f5', border: '1px solid #fecaca', borderRadius: '10px',
              padding: '12px 14px', marginBottom: '18px', display: 'flex', gap: '10px',
            }}>
              <AlertTriangle size={16} style={{ color: '#dc2626', flexShrink: 0, marginTop: '1px' }} />
              <div>
                <div style={{ fontWeight: 700, fontSize: '13px', color: '#dc2626', marginBottom: '3px' }}>Account suspended</div>
                <div style={{ fontSize: '12px', color: BODY, lineHeight: 1.5 }}>{error}</div>
                <a href={`mailto:${SUPPORT_EMAIL}`} style={{ fontSize: '11px', color: FLAME, marginTop: '4px', display: 'block' }}>
                  Contact support →
                </a>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            <div style={{ marginBottom: '16px' }}>
              <label htmlFor="ia-login-email" style={labelStyle}>Work email</label>
              <div style={{ position: 'relative' }}>
                <Mail size={15} style={iconStyle} />
                <input id="ia-login-email" style={inputStyle} type="email" placeholder="you@company.com" autoFocus
                  value={email} onChange={e => setEmail(e.target.value)} />
              </div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label htmlFor="ia-login-pw" style={labelStyle}>Password</label>
              <div style={{ position: 'relative' }}>
                <Lock size={15} style={iconStyle} />
                <input id="ia-login-pw" style={{ ...inputStyle, paddingRight: '44px' }}
                  type={showPw ? 'text' : 'password'} placeholder="••••••••"
                  value={password} onChange={e => setPassword(e.target.value)} />
                <button type="button" onClick={() => setShowPw(s => !s)}
                  aria-label={showPw ? 'Hide password' : 'Show password'}
                  style={{
                    position: 'absolute', right: '14px', top: '50%', transform: 'translateY(-50%)',
                    background: 'none', border: 'none', cursor: 'pointer', color: '#a8a297', padding: 0,
                  }}>
                  {showPw ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
            </div>

            {error && !suspended && (
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', color: '#dc2626', fontSize: '13px', marginBottom: '14px' }}>
                <AlertTriangle size={13} /> {error}
              </div>
            )}

            <button type="submit" disabled={loading} style={{
              width: '100%', height: '50px', background: FLAME, color: INK,
              fontSize: '15px', fontWeight: 600, border: 'none', borderRadius: '28px',
              cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1,
              fontFamily: "'Space Grotesk', sans-serif",
            }}>
              {loading ? 'Signing in…' : 'Sign in'}
            </button>
          </form>

          <p style={{ textAlign: 'center', marginTop: '20px', fontSize: '13px', color: MUTED }}>
            Don't have an account?{' '}
            <Link to="/contact" style={{ color: FLAME, fontWeight: 600, textDecoration: 'none' }}>
              Contact us to get access →
            </Link>
          </p>
        </div>
      </section>
    </SiteLayout>
  );
}
